import React from "react";
import { gaTrackCta } from "../../analytics/ga4";
import { pageGutterPx } from "../../core/breakpoints";
import { C, FOOTER_CONTACT_EMAIL, WZ_GLYPHS } from "../../core/constants";
import { zodiacGlyphPosition } from "../visual/zodiacRing";
import { APP_FONT_SIZE } from "../../core/theme";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { runtimeConfig } from "../../config/runtime";
import { useI18n } from "../../features/i18n/LocaleProvider";
import Button from "../../ui/Button";
import GlassCard from "../../ui/GlassCard";
import GoldText from "../visual/GoldText";
import OrbitDeco from "../visual/OrbitDeco";
import PricingSection from "../pricing/PricingSection";
import LandingSeoContent from "./LandingSeoContent";
import { orbitDecoWrapStyle, spotlightCardStyle } from "./landingSpotlightLayout";
import { localizedPath } from "../../core/localePaths";
import { weddingGuidePaths } from "../../core/weddingGuidePaths";

export default function LandingPage({ setPage, acc }) {
  const { t, locale } = useI18n();
  const guides = React.useMemo(() => weddingGuidePaths(locale), [locale]);
  const fs = APP_FONT_SIZE;
  const isMobile = useIsMobile();
  const gutter = pageGutterPx(isMobile);
  const zodiacRingSize = isMobile ? Math.min(360, typeof window !== "undefined" ? window.innerWidth - 32 : 360) : 700;
  const features = [
    { id: "western", icon: "✦", title: t("feature_western_astrology_title"), desc: t("feature_western_astrology_desc"), page: "western" },
    { id: "chinese", icon: "☯", title: t("feature_chinese_zodiac_title"), desc: t("feature_chinese_zodiac_desc"), page: "chinese" },
    { id: "tarot", icon: "⊕", title: t("feature_tarot_reading_title"), desc: t("feature_tarot_reading_desc"), page: "tarot" },
    { id: "wedding", icon: "♡", title: t("feature_wedding_compatibility_title"), desc: t("feature_wedding_compatibility_desc"), page: "wedding" },
    { id: "coupleMatch", icon: "囍", title: t("feature_couple_match_title"), desc: t("feature_couple_match_desc"), page: "coupleMatch" },
  ];
  return (
    <div style={{ position: "relative", zIndex: 1, paddingBottom: 52 }}>
      <div
        style={{
          position: "relative",
          minHeight: isMobile ? "calc(100vh + 48px)" : "calc(100vh + 72px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: isMobile ? `48px ${gutter}px 40px` : "80px 24px 60px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(ellipse 80% 60% at 50% 30%, ${acc.primary}12 0%, transparent 70%), radial-gradient(ellipse 50% 40% at 70% 70%, ${acc.tertiary}0d 0%, transparent 60%)`,
            pointerEvents: "none",
          }}
        />

        <div style={{ position: "relative", animation: "fadeInUp 1s ease both", zIndex: 1 }}>
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
              width: zodiacRingSize,
              height: zodiacRingSize,
              pointerEvents: "none",
              zIndex: 0,
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                border: `1px dashed ${acc.primary}18`,
                animation: "spin-slow 60s linear infinite",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {WZ_GLYPHS.map((z, i) => (
                <span
                  key={z}
                  style={{
                    position: "absolute",
                    ...zodiacGlyphPosition(i, 340),
                    fontSize: 22,
                    opacity: 0.25,
                    color: acc.primary,
                  }}
                >
                  {z}
                </span>
              ))}
            </div>
          </div>

          <div style={{ position: "relative", zIndex: 1 }}>
            <div
              style={{
                fontSize: 13,
                letterSpacing: "0.25em",
                color: acc.secondary,
                textTransform: "uppercase",
                marginBottom: 20,
                opacity: 0.8,
              }}
            >
              {t("hero_badge")}
            </div>
            <h1
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(52px, 8vw, 96px)",
                fontWeight: 300,
                lineHeight: 1.05,
                color: C.textPrimary,
                marginBottom: 16,
                letterSpacing: "-0.01em",
              }}
            >
              {t("hero_discover_your")}
              <br />
              <GoldText accent={acc.secondary}>{t("hero_destiny")}</GoldText>
            </h1>
            <p style={{ fontSize: fs + 2, color: C.textSecondary, maxWidth: 520, margin: "0 auto 40px", lineHeight: 1.7 }}>
              {t("hero_description")}
            </p>
            <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
              <Button
                variant="primary"
                acc={acc}
                onClick={() => {
                  if (runtimeConfig.authContext.signedIn) {
                    gaTrackCta({
                      cta_id: "landing_hero_primary",
                      cta_label: t("hero_cta_start_free_reading"),
                      destination: "dashboard",
                      screen_class: "landing",
                    });
                    setPage("dashboard");
                  } else {
                    const destination = localizedPath("signUp", locale);
                    gaTrackCta({
                      cta_id: "landing_hero_signup",
                      cta_label: t("hero_cta_start_free_reading"),
                      destination,
                      screen_class: "landing",
                    });
                    window.location.assign(destination);
                  }
                }}
                style={{ padding: "14px 36px", fontSize: fs }}
              >
                {t("hero_cta_start_free_reading")}
              </Button>
              <Button
                variant="outline"
                acc={acc}
                onClick={() => {
                  gaTrackCta({
                    cta_id: "landing_hero_wedding",
                    cta_label: t("hero_cta_explore_compatibility"),
                    destination: "wedding",
                    screen_class: "landing",
                  });
                  setPage("wedding");
                }}
                style={{ padding: "14px 36px", fontSize: fs }}
              >
                {t("hero_cta_explore_compatibility")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <LandingSeoContent acc={acc} t={t} isMobile={isMobile} gutter={gutter} fs={fs} setPage={setPage} />

      <div style={{ padding: isMobile ? `48px ${gutter}px` : "80px 40px", maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.2em", color: acc.primary, textTransform: "uppercase", marginBottom: 12 }}>
            {t("home_what_we_offer")}
          </div>
          <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(36px,5vw,56px)", fontWeight: 400, color: C.textPrimary }}>
            {t("home_full_celestial_suite")}
          </h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 24 }}>
          {features.map((f) => (
            <GlassCard
              key={f.id}
              hoverable
              accent={acc.primary}
              style={{ padding: 32, cursor: "pointer" }}
              glow
              onClick={() => {
                gaTrackCta({
                  cta_id: `landing_feature_${f.id}`,
                  cta_label: f.title,
                  destination: f.page,
                  screen_class: "landing",
                });
                setPage(f.page);
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 20, color: acc.primary }}>{f.icon}</div>
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, fontWeight: 500, color: C.textPrimary, marginBottom: 10 }}>
                {f.title}
              </h3>
              <p style={{ fontSize: fs - 1, color: C.textSecondary, lineHeight: 1.65 }}>{f.desc}</p>
            </GlassCard>
          ))}
        </div>
      </div>

      <div style={{ padding: isMobile ? `40px ${gutter}px` : "60px 40px", maxWidth: 1100, margin: "0 auto" }}>
        <GlassCard style={spotlightCardStyle(isMobile)} glow accent={acc.secondary}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontSize: 12, letterSpacing: "0.2em", color: acc.secondary, textTransform: "uppercase", marginBottom: 16 }}>
              {t("home_featured")}
            </div>
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(30px,4vw,48px)", fontWeight: 400, color: C.textPrimary, marginBottom: 16 }}>
              {t("home_plan_your_perfect")}
              <br />
              <GoldText accent={acc.secondary}>{t("home_wedding_day")}</GoldText>
            </h2>
            <p style={{ fontSize: fs, color: C.textSecondary, lineHeight: 1.7, marginBottom: 16 }}>{t("home_wedding_spotlight_desc")}</p>
            <div style={{ fontSize: fs - 1, color: C.textMuted, lineHeight: 1.65, marginBottom: 28 }}>
              <div style={{ marginBottom: 8 }}>{t("home_guide_planning_intro")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <a
                  href={guides.auspicious}
                  style={{ color: "#a8dfff", textDecoration: "underline" }}
                  onClick={() =>
                    gaTrackCta({
                      cta_id: "landing_guide_wedding_dates",
                      cta_label: t("home_guide_planning_dates"),
                      destination: guides.auspicious,
                      screen_class: "landing",
                    })
                  }
                >
                  {t("home_guide_planning_dates")}
                </a>
                <a
                  href={guides.lunar}
                  style={{ color: "#a8dfff", textDecoration: "underline" }}
                  onClick={() =>
                    gaTrackCta({
                      cta_id: "landing_guide_lunar",
                      cta_label: t("home_guide_planning_lunar"),
                      destination: guides.lunar,
                      screen_class: "landing",
                    })
                  }
                >
                  {t("home_guide_planning_lunar")}
                </a>
              </div>
            </div>
            <Button
              variant="primary"
              acc={acc}
              onClick={() => {
                gaTrackCta({
                  cta_id: "landing_spotlight_wedding",
                  cta_label: t("home_find_auspicious_dates"),
                  destination: "wedding",
                  screen_class: "landing",
                });
                setPage("wedding");
              }}
            >
              {t("home_find_auspicious_dates")}
            </Button>
          </div>
          <div style={orbitDecoWrapStyle(isMobile)}>
            <OrbitDeco acc={acc} variant="wedding" />
          </div>
        </GlassCard>
      </div>

      <div style={{ padding: isMobile ? `0 ${gutter}px 40px` : "0 40px 60px", maxWidth: 1100, margin: "0 auto" }}>
        <GlassCard style={spotlightCardStyle(isMobile)} glow accent={acc.tertiary}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontSize: 12, letterSpacing: "0.2em", color: acc.tertiary, textTransform: "uppercase", marginBottom: 16 }}>
              {t("home_couple_match_eyebrow")}
            </div>
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(30px,4vw,48px)", fontWeight: 400, color: C.textPrimary, marginBottom: 16 }}>
              {t("home_couple_match_headline")}
              <br />
              <GoldText accent={acc.tertiary}>{t("home_couple_match_headline_accent")}</GoldText>
            </h2>
            <p style={{ fontSize: fs, color: C.textSecondary, lineHeight: 1.7, marginBottom: 28 }}>{t("home_couple_match_spotlight_desc")}</p>
            <Button
              variant="primary"
              acc={acc}
              onClick={() => {
                gaTrackCta({
                  cta_id: "landing_spotlight_couple_match",
                  cta_label: t("home_couple_match_cta"),
                  destination: "coupleMatch",
                  screen_class: "landing",
                });
                setPage("coupleMatch");
              }}
            >
              {t("home_couple_match_cta")}
            </Button>
          </div>
          <div style={orbitDecoWrapStyle(isMobile)}>
            <OrbitDeco acc={acc} variant="natal" />
          </div>
        </GlassCard>
      </div>

      <PricingSection acc={acc} variant="compact" analyticsScreen="landing" />

      <div style={{ textAlign: "center", padding: isMobile ? `20px ${gutter}px` : "20px 40px", borderTop: "1px solid rgba(255,255,255,0.06)", color: C.textMuted, fontSize: 13 }}>
        <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, color: acc.primary, marginRight: 12 }}>Ecliptica</span>© 2026 ·{" "}
        {t("all_rights_reserved")} · {FOOTER_CONTACT_EMAIL}
      </div>
    </div>
  );
}
